import type { ChronicleEventType } from '../facts';
import type { MapLink } from '../map/links';
import type { IsoDateString } from './common';

/** Filter chip labels, in the order the chips appear. */
export const CHRONICLE_TYPE_LABELS: Record<ChronicleEventType, string> = {
  level_up: 'Levels',
  area_discovered: 'Discoveries',
  zone_first_visit: 'First visits',
  rare_killed: 'Rares',
  pet_tamed: 'Tames',
  book_read: 'Books',
  profession_rank: 'Profession ranks',
  profession_specialized: 'Specializations',
  dungeon_cleared: 'Dungeons',
  death: 'Deaths',
  epic_loot: 'Epic loot',
  legacy_point: 'Legacy points',
  legacy_reward: 'Legacy rewards',
};

/** One Chronicle entry (SPEC §6.6). */
export interface ChronicleEntry {
  id: string;
  type: ChronicleEventType;
  occurredAt: IsoDateString;
  /** Derived from a snapshot diff: the time is the logout it was seen at, shown as "around". */
  approx: boolean;
  text: string;
  /** Null for account-wide events: a Legacy reward, or a point no character can be tied to. */
  characterId: string | null;
  characterName: string | null;
  /** Where it happened on the map (MAP3); null when it has no place there. */
  map: MapLink | null;
  /** Screenshots taken of it (M12), oldest first. */
  photoIds: string[];
}

/** A reset week that has entries, newest first. */
export interface ChronicleWeek {
  /** "2026-11-18": the UTC date of the week's reset. */
  id: string;
  startAt: IsoDateString;
  endAt: IsoDateString;
  count: number;
}

/**
 * `GET /api/chronicle?characterId&type&week&cursor&limit`. `characterId` and `type` take
 * comma-separated lists.
 */
export interface ChronicleResponse {
  entries: ChronicleEntry[];
  /** Pass back as `cursor` for the next, older page; null on the last page. */
  nextCursor: string | null;
  /** Every week with entries, ignoring the filters. */
  weeks: ChronicleWeek[];
  /** Entry counts in the selected week (or all time), for the filter chips. */
  facets: {
    characters: { id: string; name: string; count: number }[];
    types: { type: ChronicleEventType; count: number }[];
  };
}
